// frontend-dash/src/utils/permissions.js
// Client-side permission checking — mirrors backend/config/permissions.js

export const PERMISSIONS = {
  // Blog Permissions
  BLOG_CREATE: 'blog.create',
  BLOG_EDIT: 'blog.edit',
  BLOG_DELETE: 'blog.delete',
  BLOG_PUBLISH: 'blog.publish',
  BLOG_SUBMIT: 'blog.submit',

  // Event Permissions
  EVENT_CREATE: 'event.create',
  EVENT_EDIT: 'event.edit',
  EVENT_DELETE: 'event.delete',

  // User Management
  USER_VIEW: 'user.view',
  USER_MANAGE: 'user.manage',
  USER_DELETE: 'user.delete',

  // Child Management
  CHILD_MANAGE: 'child.manage',

  // Permission Management
  PERMISSIONS_MANAGE: 'permissions.manage',

  // Admin Access
  ADMIN_DASHBOARD: 'admin.dashboard',
};

export const ROLE_PRESETS = {
  admin: ['*'], // Wildcard for all
  gruppenleiter: [
    'blog.create',
    'blog.edit',
    'blog.publish',
    'event.create',
    'event.edit',
    'user.view',
  ],
  eltern: [
    'child.manage',
  ],
  kind: [
    'blog.submit',
  ],
};

/**
 * Check if a user has a specific permission.
 * @param {Object} user - The user object (from AuthContext or profile)
 * @param {String} permission - The permission string to check
 * @returns {Boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;
  const userRole = user.role || 'kind';
  const rolePermissions = ROLE_PRESETS[userRole] || [];
  const customPermissions = user.customPermissions || [];

  // Wildcard check
  if (rolePermissions.includes('*') || customPermissions.includes('*'))
    return true;

  return (
    rolePermissions.includes(permission) ||
    customPermissions.includes(permission)
  );
};

/**
 * Check if user can edit a specific content type on a given page.
 * @param {Object} user - The user object
 * @param {String} contentType - 'event', 'blog', or 'team'
 * @param {Object} options - Additional options (e.g., ownerId for team)
 * @returns {Boolean}
 */
export const canEditContent = (user, contentType, options = {}) => {
  if (!user) return false;

  switch (contentType) {
    case 'event':
      return hasPermission(user, PERMISSIONS.EVENT_EDIT);
    case 'blog':
      return hasPermission(user, PERMISSIONS.BLOG_EDIT);
    case 'team':
      // Admins can edit any profile; users can edit their own
      if (hasPermission(user, PERMISSIONS.USER_MANAGE)) return true;
      if (user.role === 'admin' || user.role === 'gruppenleiter') return true;
      // Check if it's the user's own profile
      if (options.ownerId && user._id === options.ownerId) return true;
      return false;
    default:
      return false;
  }
};

/**
 * Check if a user is a leader (admin or gruppenleiter).
 */
export const isLeader = (user) => {
  if (!user) return false;
  return user.role === 'admin' || user.role === 'gruppenleiter';
};

/**
 * Get a human-readable German label for a role.
 */
export const getRoleLabel = (role) => {
  const labels = {
    admin: 'Admin',
    gruppenleiter: 'Gruppenleiter',
    eltern: 'Elternteil',
    kind: 'Kind',
  };
  return labels[role] || role;
};

